export type HistorySnapshot<T> = {
  label: string;
  state: T;
};

export type HistoryState = {
  canRedo: boolean;
  canUndo: boolean;
  lastRedoLabel: string | null;
  lastUndoLabel: string | null;
  redoDepth: number;
  undoDepth: number;
};

export type HistoryManager<T> = {
  clear(): void;
  getState(): HistoryState;
  pushUndoSnapshot(label: string, state: T): void;
  redo(current: T): T | null;
  undo(current: T): T | null;
};

export function createHistoryManager<T>(cloneState: (state: T) => T, limit = 64): HistoryManager<T> {
  const undoStack: Array<HistorySnapshot<T>> = [];
  const redoStack: Array<HistorySnapshot<T>> = [];

  const trimToLimit = (): void => {
    if (undoStack.length > limit) {
      undoStack.splice(0, undoStack.length - limit);
    }
  };

  const makeState = (): HistoryState => ({
    canRedo: redoStack.length > 0,
    canUndo: undoStack.length > 0,
    lastRedoLabel: redoStack.length > 0 ? redoStack[redoStack.length - 1]!.label : null,
    lastUndoLabel: undoStack.length > 0 ? undoStack[undoStack.length - 1]!.label : null,
    redoDepth: redoStack.length,
    undoDepth: undoStack.length
  });

  return {
    clear() {
      undoStack.length = 0;
      redoStack.length = 0;
    },
    getState() {
      return makeState();
    },
    pushUndoSnapshot(label, state) {
      undoStack.push({ label, state: cloneState(state) });
      trimToLimit();
      redoStack.length = 0;
    },
    redo(current) {
      const snapshot = redoStack.pop();
      if (snapshot === undefined) {
        return null;
      }
      undoStack.push({ label: snapshot.label, state: cloneState(current) });
      trimToLimit();
      return cloneState(snapshot.state);
    },
    undo(current) {
      const snapshot = undoStack.pop();
      if (snapshot === undefined) {
        return null;
      }
      redoStack.push({ label: snapshot.label, state: cloneState(current) });
      return cloneState(snapshot.state);
    }
  };
}
